/**
 * Sanitización de datos - Previene XSS y ataques de inyección
 */

/**
 * Escapa caracteres HTML peligrosos
 */
export function escapeHtml(str: string): string {
  if (!str) return '';
  const htmlEscapes: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
    '/': '&#x2F;',
  };
  return str.replace(/[&<>"'/]/g, (char) => htmlEscapes[char]);
}

/**
 * Elimina tags HTML de un string
 */
export function stripHtml(str: string): string {
  if (!str) return '';
  return str.replace(/<[^>]*>/g, '');
}

/**
 * Sanitiza un string para uso seguro
 */
export function sanitizeString(str: string, options: {
  maxLength?: number;
  allowHtml?: boolean;
  trim?: boolean;
} = {}): string {
  if (!str) return '';
  
  let result = str;
  
  // Trim por defecto
  if (options.trim !== false) {
    result = result.trim();
  }
  
  // Limitar longitud
  if (options.maxLength && result.length > options.maxLength) {
    result = result.substring(0, options.maxLength);
  }
  
  // Escapar HTML si no está permitido
  if (!options.allowHtml) {
    result = escapeHtml(result);
  }
  
  return result;
}

/**
 * Sanitiza un email
 */
export function sanitizeEmail(email: string): string {
  if (!email) return '';
  return email.toLowerCase().trim().replace(/[^a-z0-9@._+-]/g, '');
}

/**
 * Sanitiza un número de teléfono
 */
export function sanitizePhone(phone: string): string {
  if (!phone) return '';
  return phone.replace(/[^0-9+]/g, '');
}

/**
 * Sanitiza un objeto recursivamente
 */
export function sanitizeObject<T extends Record<string, unknown>>(obj: T): T {
  const result: Record<string, unknown> = {};
  
  for (const [key, value] of Object.entries(obj)) {
    if (typeof value === 'string') {
      result[key] = sanitizeString(value);
    } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      result[key] = sanitizeObject(value as Record<string, unknown>);
    } else if (Array.isArray(value)) {
      result[key] = value.map(item => 
        typeof item === 'string' ? sanitizeString(item) :
        typeof item === 'object' && item !== null ? sanitizeObject(item as Record<string, unknown>) :
        item
      );
    } else {
      result[key] = value;
    }
  }
  
  return result as T;
}

/**
 * Previene prototype pollution
 */
export function safeJsonParse<T>(json: string): T | null {
  try {
    const parsed = JSON.parse(json);
    
    // Verificar prototype pollution
    const dangerous = ['__proto__', 'constructor', 'prototype'];
    const checkObject = (obj: unknown): boolean => {
      if (typeof obj !== 'object' || obj === null) return true;
      for (const key of Object.keys(obj as Record<string, unknown>)) {
        if (dangerous.includes(key)) return false;
        if (!checkObject((obj as Record<string, unknown>)[key])) return false;
      }
      return true;
    };
    
    if (!checkObject(parsed)) {
      console.error('Potential prototype pollution detected');
      return null;
    }
    
    return parsed as T;
  } catch {
    return null;
  }
}
